import { Injectable, inject } from '@angular/core';
import { EnvironmentService } from 'projects/bis-shared/src/lib/environment/environment.service';
import { Environment } from 'projects/bis-shared/src/lib/environment/environment';
import {
  EntityTypeCode,
  SystemTypeCode,
  ObjectTypeCode,
  EntityTypeName,
} from 'projects/bis-shared/src/lib/utility/system-type-constants';
import { TransactionType } from 'projects/bis-shared/src/lib/utility/transaction-constants';

/**************************************
 *
 *  This naming convention is temporary
 *  until we can fully refactor apiurls
 *  into Internal and External
 *
 **************************************/

@Injectable({
  providedIn: 'root',
})
export class InternalApiUrls {

  
 

  constructor() {
    const environmentService = inject(EnvironmentService);

    const config = environmentService.getEnvironment();

    // Try to keep this in alpha order to avoid dups
    this.address._initialize(config);
    this.audit._initialize(config);
    this.claimant._initialize(config);
    this.contact._initialize(config);
    this.directory._initialize(config);
    this.document._initialize(config);
    this.employer._initialize(config);
    this.entityUser._initialize(config);
    this.healthcareProvider._initialize(config);
    this.healthcareProviderLicenseSpecialty._initialize(config);
    this.healthcareProviderLicenseImpartialSpecialist._initialize(config);
    this.healthcareProviderRelationship._initialize(config);
    this.insurer._initialize(config);
    this.medicalDirectorsOfficeReview._initialize(config);
    this.medicalSupplier._initialize(config);
    this.medicalBillingDispute._initialize(config);
    this.note._initialize(config);
    this.objectSubType._initialize(config);
    this.queue._initialize(config);
    this.reference._initialize(config);
    this.standardWorkRequest._initialize(config);
    this.systemAccessGroup._initialize(config);
    this.thirdPartyAdministrator._initialize(config);
    this.user._initialize(config);
    this.userSecurity._initialize(config);
    this.workGroup._initialize(config);
    this.workRequestAssignee._initialize(config);
    this.standardWorkRequest._initialize(config);
    this.workflow._initialize(config);
    this.workRequest._initialize(config);
    this.workRequestAssignee._initialize(config);
    this.workRequestConfiguration._initialize(config);
    this.priorAuthorization._initialize(config);
    this.priorAuthorizationResponse._initialize(config);
    this.sender._initialize(config);
    this.jobManagement._initialize(config);
  }

  address = {
    getAddresses() {
      return `${this.addressUrl}/search`;
    },
    updateAddress(addressId: number) {
      return `${this.addressUrl}/${addressId}`;
    },
    createAddress() {
      return `${this.addressUrl}`;
    },
    updatePrimaryAddress() {
      return `${this.addressUrl}/updateprimary`;
    },
    deleteAddress(addressId: number) {
      return `${this.addressUrl}/${addressId}/delete`;
    },
    _initialize(config: Environment) {
      this.addressUrl = config.baseUrls.entityApiUrl + '/api/address';
    },
  };

  audit = {
    audit(typeName: string, objectTypeCode: ObjectTypeCode) {
      typeName = typeName.replace(/\s/g, '');
      if (
        objectTypeCode === ObjectTypeCode.MedicalBillingDispute ||
        objectTypeCode === ObjectTypeCode.PriorAuthorization ||
        objectTypeCode === ObjectTypeCode.MdoReviewRequest
      ) {
        return `${this.medicalAuditUrl}/${typeName.trim()}`;
      } else if (objectTypeCode === ObjectTypeCode.Document) {
        return `${this.documentAuditUrl}/${typeName}`;
      } else {
        return `${this.entityAuditUrl}/${typeName}`;
      }
    },
    _initialize(config: Environment) {
      this.entityAuditUrl = config.baseUrls.entityApiUrl + '/api/audit';
      this.documentAuditUrl = config.baseUrls.documentApiUrl + '/api/audit';
      this.medicalAuditUrl = config.baseUrls.medicalApiUrl + '/api/audit';
    },
  };

  claimant = {
    get(id: number) {
      return `${this.claimantUrl}/${id}`;
    },
    update(id: number) {
      return `${this.claimantUrl}/${id}`;
    },
    create() {
      return `${this.claimantUrl}`;
    },
    search() {
      return `${this.claimantUrl}/search`;
    },
    _initialize(config: Environment) {
      this.claimantUrl = config.baseUrls.entityApiUrl + '/api/claimant';
    },
  };

  entityUser = {
    getEntityUsers(entityTypeName: EntityTypeName) {
      return `${this.entityUserUrl}/${entityTypeName}User/search`;
    },
    _initialize(config: Environment) {
      this.entityUserUrl = config.baseUrls.userApiUrl + '/api';
    },
  };

  contact = {
    getContacts(entityTypeName: EntityTypeName) {
      return `${this.contactUrl}/${entityTypeName}Contact/search`;
    },
    getContact(entityTypeName: EntityTypeName, contactId: number) {
      return `${this.contactUrl}/${entityTypeName}Contact/${contactId}`;
    },
    createContact(entityTypeName: EntityTypeName) {
      return `${this.contactUrl}/${entityTypeName}Contact/`;
    },
    updateContact(entityTypeName: EntityTypeName, contactId: number) {
      return `${this.contactUrl}/${entityTypeName}Contact/${contactId}`;
    },
    deleteContact(entityTypeName: EntityTypeName, contactId: number) {
      return `${this.contactUrl}/${entityTypeName}Contact/${contactId}`;
    },
    _initialize(config: Environment) {
      this.contactUrl = config.baseUrls.entityApiUrl + '/api';
    },
  };

  healthcareProvider = {
    get(id: number) {
      return `${this.healthcareProviderUrl}/${id}`;
    },
    update(id: number) {
      return `${this.healthcareProviderUrl}/${id}`;
    },
    create() {
      return `${this.healthcareProviderUrl}`;
    },
    search() {
      return `${this.healthcareProviderUrl}/search`;
    },
    lookup() {
      return `${this.healthcareProviderUrl}/lookup`;
    },
    license(licenseId: number) {
      return `${this.healthcareProviderUrl}/license/${licenseId}`;
    },
    licenses(healthcareProviderId: number) {
      return `${this.healthcareProviderUrl}/${healthcareProviderId}/licenses`;
    },
    _initialize(config: Environment) {
      this.healthcareProviderUrl = config.baseUrls.entityApiUrl + '/api/healthcareProvider';
    },
  };

  healthcareProviderLicenseSpecialty = {
    search() {
      return `${this.healthcareProviderLicenseSpecialtyUrl}/search`;
    },
    create() {
      return `${this.healthcareProviderLicenseSpecialtyUrl}`;
    },
    update(id: number) {
      return `${this.healthcareProviderLicenseSpecialtyUrl}/${id}`;
    },
    delete() {
      return `${this.healthcareProviderLicenseSpecialtyUrl}/delete`;
    },
    _initialize(config: Environment) {
      this.healthcareProviderLicenseSpecialtyUrl =
        config.baseUrls.entityApiUrl + '/api/healthcareProviderLicenseSpecialty';
    },
  };

  healthcareProviderLicenseImpartialSpecialist = {
    search() {
      return `${this.healthcareProviderLicenseImpartialSpecialistDesignationUrl}/search`;
    },
    create() {
      return `${this.healthcareProviderLicenseImpartialSpecialistDesignationUrl}`;
    },
    update(id: number) {
      return `${this.healthcareProviderLicenseImpartialSpecialistDesignationUrl}/${id}`;
    },
    delete() {
      return `${this.healthcareProviderLicenseImpartialSpecialistDesignationUrl}/delete`;
    },
    _initialize(config: Environment) {
      this.healthcareProviderLicenseImpartialSpecialistDesignationUrl =
        config.baseUrls.entityApiUrl +
        '/api/healthcareProviderLicenseImpartialSpecialistDesignation';
    },
  };

  healthcareProviderRelationship = {
    providerSupervisorylookup() {
      return `${this.healthcareProviderRelationshipUrl}/provider-lookup`;
    },
    getRelationships() {
      return `${this.healthcareProviderRelationshipUrl}/relationships`;
    },
    getMedicalSupplierRelationship(healthcareProviderId: number, medicalSupplierId: number) {
      return `${this.healthcareProviderRelationshipUrl}/${healthcareProviderId}/${medicalSupplierId}`;
    },
    getProviderSupervisoryRelationship(licenseId: number, supervisoryLicenseId: number) {
      return `${this.healthcareProviderRelationshipUrl}/${licenseId}/${supervisoryLicenseId}`;
    },
    createMedicalSupplierRelationships() {
      return `${this.healthcareProviderRelationshipUrl}/create/medical-supplier`;
    },
    createProviderSupervisoryRelationships() {
      return `${this.healthcareProviderRelationshipUrl}/create/provider-supervisory`;
    },
    updateMedicalSupplierRelationships() {
      return `${this.healthcareProviderRelationshipUrl}/update/medical-supplier`;
    },
    updateProviderSupervisoryRelationships() {
      return `${this.healthcareProviderRelationshipUrl}/update/provider-supervisory`;
    },
    deleteMedicalSupplierRelationships() {
      return `${this.healthcareProviderRelationshipUrl}/delete/medical-supplier`;
    },
    deleteProviderSupervisoryRelationships() {
      return `${this.healthcareProviderRelationshipUrl}/delete/provider-supervisory`;
    },
    _initialize(config: Environment) {
      this.healthcareProviderRelationshipUrl =
        config.baseUrls.entityApiUrl + '/api/healthcareProviderRelationship';
    },
  };

  insurer = {
    get(id: number) {
      return `${this.insurerUrl}/${id}`;
    },
    search() {
      return `${this.insurerUrl}/search`;
    },
    lookup() {
      return `${this.insurerUrl}/lookup`;
    },
    getInsurerGroups() {
      return `${this.insurerUrl}/insurergroups`;
    },
    getInsurerAffiliations() {
      return `${this.insurerUrl}/insureraffiliations`;
    },
    getRelationships() {
      return `${this.insurerUrl}/relationships`;
    },
    _initialize(config: Environment) {
      this.insurerUrl = config.baseUrls.entityApiUrl + '/api/insurer';
    },
  };

  employer = {
    get(id: number) {
      return `${this.employerUrl}/${id}`;
    },
    search() {
      return `${this.employerUrl}/search`;
    },
    lookup() {
      return `${this.employerUrl}/search`;
    },
    create() {
      return `${this.employerUrl}`;
    },
    update(id: number) {
      return `${this.employerUrl}/${id}`;
    },
    _initialize(config: Environment) {
      this.employerUrl = config.baseUrls.entityApiUrl + '/api/employer';
    },
  };

  jobManagement = {
    getJobs() {
      return `${this.jobManagementUrl}/jobs-by-status`;
    },
    getJobsByPriorAuthorizationId(priorAuthorizationId: number) {
      return `${this.jobManagementUrl}/jobsByPriorAuthorizationId/${priorAuthorizationId}`;
    },
    getJobDetails(jobName: string) {
      return `${this.jobManagementUrl}/job-details/${encodeURIComponent(jobName)}`;
    },
    getJobExceptions(jobName: string) {
      return `${this.jobManagementUrl}/job-details/${encodeURIComponent(jobName)}/exceptions`;
    },
    getJobStats(jobName: string) {
      return `${this.jobManagementUrl}/job-details/${encodeURIComponent(jobName)}/job-stats`;
    },
    getJobSteps(jobId: number) {
      return `${this.jobManagementUrl}/job/${jobId}/job-steps`;
    },
    resumeFailedJob(jobId: number) {
      return `${this.jobManagementUrl}/resumefailedjob/${jobId}`;
    },
    resumeAllFailedJobs(jobName: string) {
      return `${this.jobManagementUrl}/resumeallfailedjobs/${jobName}`;
    },
    _initialize(config: Environment) {
      this.jobManagementUrl = config.baseUrls.jobManagementUrl + '/api/jobManagement';
    },
  };

  medicalSupplier = {
    search() {
      return `${this.medicalSupplierUrl}/search`;
    },
    get(id: number) {
      return `${this.medicalSupplierUrl}/${id}`;
    },
    create() {
      return `${this.medicalSupplierUrl}`;
    },
    update(id: number) {
      return `${this.medicalSupplierUrl}/${id}`;
    },
    lookup() {
      return `${this.medicalSupplierUrl}/lookup`;
    },
    _initialize(config: Environment) {
      this.medicalSupplierUrl = config.baseUrls.entityApiUrl + '/api/medicalsupplier';
    },
  };

  note = {
    getNotes(entityType: string, entityId: number) {
      return `${this.notesUrl}/${entityType}/${entityId}`;
    },
    getNoteHistory(entityType: string, entityId: number, noteId: number) {
      return `${this.notesUrl}/${entityType}/${entityId}/${noteId}/history`;
    },
    createNote(entityType: string) {
      return `${this.notesUrl}/${entityType}`;
    },
    updateNote(entityType: string, entityId: number, noteId: number) {
      return `${this.notesUrl}/${entityType}/${entityId}/${noteId}`;
    },
    deleteNote(entityType: string, entityId: number, noteId: number) {
      return `${this.notesUrl}/${entityType}/${entityId}/${noteId}`;
    },
    _initialize(config: Environment) {
      this.notesUrl = config.baseUrls.noteApiUrl + '/api/note';
    },
  };

  thirdPartyAdministrator = {
    get(id: number) {
      return `${this.thirdPartyAdministratorUrl}/${id}`;
    },
    update(id: number) {
      return `${this.thirdPartyAdministratorUrl}/${id}`;
    },
    create() {
      return `${this.thirdPartyAdministratorUrl}/create`;
    },
    search() {
      return `${this.thirdPartyAdministratorUrl}/search`;
    },
    lookup() {
      return `${this.thirdPartyAdministratorUrl}/lookup`;
    },
    getRelationships() {
      return `${this.thirdPartyAdministratorUrl}/relationships`;
    },
    _initialize(config: Environment) {
      this.thirdPartyAdministratorUrl =
        config.baseUrls.entityApiUrl + '/api/thirdPartyAdministrator';
    },
  };

  workGroup = {
    getSystemUsers(id: number): string {
      return `${this.workGroupUrl}/${id}/systemUsers`;
    },
    getWorkGroupAndAssignedUsers(id: number, workGroupId: number): string {
      return `${this.workGroupUrl}/user/${id}/${workGroupId}`;
    },
    getWorkGroups() {
      return `${this.workGroupUrl}/workGroups`;
    },
    getAssignorOrSupervisorWorkgroups() {
      return `${this.workGroupUrl}/assignorOrSupervisorWorkgroups`;
    },
    search() {
      return `${this.workGroupUrl}/search`;
    },
    get(id: number) {
      return `${this.workGroupUrl}/${id}`;
    },
    create() {
      return `${this.workGroupUrl}/create`;
    },
    update() {
      return `${this.workGroupUrl}/update`;
    },
    workGroupAllocations() {
      return `${this.workGroupUrl}/workgroup-allocation`;
    },
    updateWorkGroupAllocations() {
      return `${this.workGroupUrl}/update-workgroup-allocation`;
    },
    _initialize(config: Environment) {
      this.workGroupUrl = config.baseUrls.userApiUrl + '/api/workgroup';
    },
  };

  userSecurity = {
    search() {
      return `${this.userUrl}/search`;
    },
    create() {
      return `${this.userUrl}/create`;
    },
    update() {
      return `${this.userUrl}/update`;
    },
    get(id: number) {
      return `${this.userUrl}/${id}`;
    },
    _initialize(config: Environment) {
      this.userUrl = config.baseUrls.userApiUrl + '/api/usersecurity';
    },
  };

  queue = {
    search() {
      return `${this.queueApiUrl}/search`;
    },
    update() {
      return `${this.queueApiUrl}/update`;
    },
    detail(id: number) {
      return `${this.queueApiUrl}/${id}`;
    },
    getActive(): string {
      return `${this.queueApiUrl}`;
    },
    _initialize(config: Environment) {
      this.queueApiUrl = config.baseUrls.userApiUrl + '/api/queue';
    },
  };

  objectSubType = {
    getByObjectType(objectType: string) {
      return `${this.objectSubTypeApiUrl}/${objectType}`;
    },
    _initialize(config: Environment) {
      this.objectSubTypeApiUrl = config.baseUrls.refApiUrl + '/api/objectSubType';
    },
  };

  standardWorkRequest = {
    get(id: number) {
      return `${this.standardWorkRequestUrl}/${id}`;
    },
    save() {
      return `${this.standardWorkRequestUrl}/`;
    },
    getAll() {
      return `${this.standardWorkRequestUrl}/search`;
    },
    deactivate(id: number) {
      return `${this.standardWorkRequestUrl}/${id}/deactivate`;
    },
    activate(id: number) {
      return `${this.standardWorkRequestUrl}/${id}/activate`;
    },
    listByWorkGroup(id: number) {
      return `${this.standardWorkRequestUrl}/workgroup/${id}/list`;
    },
    list() {
      return `${this.standardWorkRequestUrl}/list`;
    },
    _initialize(config: Environment) {
      this.standardWorkRequestUrl = config.baseUrls.workflowApiUrl + '/api/standardworkrequest';
    },
  };

  medicalBillingDispute = {
    get(id: number) {
      return `${this.medicalBillingDisputeUrl}/medicalBillingDispute/${id}`;
    },
    _initialize(config: Environment) {
      this.medicalBillingDisputeUrl = config.baseUrls.medicalApiUrl + '/api';
    },
  };

  medicalDirectorsOfficeReview = {
    get(id: number) {
      return `${this.mdoReviewUrl}/${id}`;
    },
    update(id: number) {
      return `${this.mdoReviewUrl}/${id}`;
    },
    bulkUpdate() {
      return `${this.mdoReviewUrl}/bulkedit`;
    },
    search() {
      return `${this.mdoReviewUrl}/search`;
    },
    getAssociatedMdoReviewForPriorAuth(id: number) {
      return `${this.mdoReviewUrl}/${id}/associatedMdoReview`;
    },
    getConfirmationDeterminations(id: number) {
      return `${this.mdoReviewUrl}/confirmationDeterminations/${id}`;
    },
    getDmeDeterminations(id: number) {
      return `${this.mdoReviewUrl}/dmeDeterminations/${id}`;
    },
    getMedicationDeterminations(id: number) {
      return `${this.mdoReviewUrl}/medicationDeterminations/${id}`;
    },
    getSpecialServicesDeterminations(id: number) {
      return `${this.mdoReviewUrl}/specialServicesConfirmations/${id}`;
    },
    getVarianceDeterminations(id: number) {
      return `${this.mdoReviewUrl}/varianceDeterminations/${id}`;
    },
    saveDetermination() {
      return `${this.mdoReviewUrl}/determination`;
    },
    preview() {
      return `${this.mdoReviewUrl}/DecisionLetter/Preview`;
    },
    notifyParties() {
      return `${this.mdoReviewUrl}/DecisionLetter/Resolution`;
    },
    queue() {
      return `${this.mdoReviewUrl}/queue`;
    },
    _initialize(config: Environment) {
      this.mdoReviewUrl = config.baseUrls.medicalApiUrl + '/api/medicalDirectorsOfficeReview';
    },
  };

  priorAuthorization = {
    get(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${id}`;
    },
    search() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/search`;
    },
    healthcareProviderPriorAuth(healthcareProviderId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/healthcareProviderPriorAuth/${healthcareProviderId}`;
    },
    getRelatedRequests(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedPriorAuthRequests/${priorAuthId}`;
    },
    durableMedicalEquipmentLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/durable-medical-equipment`;
    },
    durableMedicalEquipmentSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/durable-medical-equipment-with-responses`;
    },
    medicationLineItem(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/medication`;
    },
    medicationSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/medication-with-responses`;
    },
    mtgLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/mtg`;
    },
    mtgSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/mtg-with-responses`;
    },
    nonMtgSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/non-mtg-with-responses`;
    },
    nonMtgLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/non-mtg`;
    },
    specialServiceLineItems(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/special-service`;
    },
    specialServiceSubsection(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/line-items/special-service-with-responses`;
    },
    correspondenceHistory(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${id}/correspondence-history`;
    },
    getRelatedClaimant(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedClaimant/${id}`;
    },
    getRelatedHealthcareProvider(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedHealthcareProvider/${id}`;
    },
    getRelatedInsurer(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedInsurer/${id}`;
    },
    getRelatedSenderEntity(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedSenderEntity/${id}`;
    },
    getRelatedClaimAdminEntity(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/${priorAuthId}/relatedClaimAdminEntity`;
    },
    getRelatedSenderUsers(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedSenderUsers/${id}`;
    },
    getRelatedUsersForNotification(id: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/relatedEntityusersForNotification/${id}`;
    },
    notifyUsersDocumentUpload() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/notifyUsersDocumentUpload`;
    },
    getSenderClaimAdmins() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/senderClaimAdmins`;
    },
    updateSender() {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/updateSender`;
    },
    rejectSender(priorAuthId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/RejectRequestInsurerReviewRequest/${priorAuthId}`;
    },
    getProviderInformationResponseDetails(priorAuthId: number, workRequestId: number) {
      return `${this.priorAuthorizationApiUrl}/priorauthorization/providerInformationResponse/${priorAuthId}/${workRequestId}`;
    },
    _initialize(config: Environment) {
      this.priorAuthorizationApiUrl = config.baseUrls.medicalApiUrl + '/api';
    },
  };

  sender = {
    getInsurersBySenderNum(senderNum: number) {
      return `${this.senderUrl}/${senderNum}/insurers`;
    },
    getClaimAdminsBySenderNum(senderNum: number) {
      return `${this.senderUrl}/${senderNum}/claimAdmins`;
    },
    _initialize(config: Environment) {
      this.senderUrl = config.baseUrls.entityApiUrl + '/api/sender';
    },
  };

  priorAuthorizationResponse = {
    getDmeResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/dme/${objectTypeCode}`;
    },
    getMedicationResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/medication/${objectTypeCode}`;
    },
    getMtgResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/mtg/${objectTypeCode}`;
    },
    getNonMtgResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/nonMtg/${objectTypeCode}`;
    },
    getSpecialServiceResponses(priorAuthId: number, objectTypeCode: string = '') {
      return `${this.priorAuthorizationResponseUrl}/${priorAuthId}/lineItems/specialService/${objectTypeCode}`;
    },
    _initialize(config: Environment) {
      this.priorAuthorizationResponseUrl =
        config.baseUrls.medicalApiUrl + '/api/priorauthorizationresponse';
    },
  };

  workflow = {
    updateStatus() {
      return `${this.workflowUrl}/updatestatus`;
    },
    bulkUpdateStatus() {
      return `${this.workflowUrl}/bulkupdatestatus`;
    },
    reassign() {
      return `${this.workflowUrl}/reassign`;
    },
    _initialize(config: Environment) {
      this.workflowUrl = config.baseUrls.workflowApiUrl + '/api/workflow';
    },
  };

  workRequest = {
    get(id: number) {
      return `${this.workRequestUrl}/${id}`;
    },
    update(id: number) {
      return `${this.workRequestUrl}/${id}`;
    },
    bulkUpdate() {
      return `${this.workRequestUrl}/bulkedit`;
    },
    create() {
      return `${this.workflowUrl}`;
    },
    getMyWorkRequests() {
      return `${this.workRequestUrl}/myqueue`;
    },
    getWorkRequestCount(id: number) {
      return `${this.workRequestUrl}/objectStatus/${id}/count`;
    },
    getStandardWorkRequestsList() {
      return `${this.standardWorkRequestUrl}/list`;
    },
    getStandardWorkRequestsListByWorkgroup(id: number) {
      return `${this.standardWorkRequestUrl}/workgroup/${id}/list`;
    },
    getMyWorkgroupWorkRequests() {
      return `${this.workRequestUrl}/myworkgroupqueue`;
    },
    getEntityWorkRequests(typeCode: string, entityId: number) {
      return `${this.workRequestUrl}/entity/${typeCode}/${entityId}`;
    },
    getObjectWorkRequests(typeCode: string, objectId: number) {
      return `${this.workRequestUrl}/object/${typeCode}/${objectId}`;
    },
    getExternalObjectWorkRequests(typeCode: string, objectId: number) {
      return `${this.workRequestUrl}/object/external/${typeCode}/${objectId}`;
    },
    getLatestObjectWorkRequest(typeCode: string, objectId: number) {
      return `${this.workRequestUrl}/object/latest/${typeCode}/${objectId}`;
    },
    getAdjustedDate() {
      return `${this.workRequestUrl}/adjustedDate`;
    },
    _initialize(config: Environment) {
      this.workflowUrl = config.baseUrls.workflowApiUrl + '/api/workrequest';
      this.standardWorkRequestUrl = config.baseUrls.workflowApiUrl + '/api/standardworkrequest';
      this.workRequestUrl = config.baseUrls.workflowApiUrl + '/api/workrequest';
    },
  };

  workRequestAssignee = {
    getWorkRequestAssignees(isAssignee: boolean) {
      return `${this.workRequestAssigneeUrl}/${isAssignee}`;
    },
    _initialize(config: Environment) {
      this.workRequestAssigneeUrl = config.baseUrls.refApiUrl + '/api/workRequestAssignee';
    },
  };

  workRequestConfiguration = {
    getAll() {
      return `${this.workRequestConfigurationUrl}/search`;
    },
    save() {
      return `${this.workRequestConfigurationUrl}/save`;
    },
    get(id: number) {
      return `${this.workRequestConfigurationUrl}/${id}`;
    },
    deactivate(id: number) {
      return `${this.workRequestConfigurationUrl}/${id}/deactivate`;
    },
    activate(id: number) {
      return `${this.workRequestConfigurationUrl}/${id}/activate`;
    },
    _initialize(config: Environment) {
      this.workRequestConfigurationUrl =
        config.baseUrls.workflowApiUrl + '/api/workrequestconfiguration';
    },
  };

  systemAccessGroup = {
    update() {
      return `${this.systemAccessGroupApiUrl}/update`;
    },
    detail(id: number) {
      return `${this.systemAccessGroupApiUrl}/${id}`;
    },
    _initialize(config: Environment) {
      this.systemAccessGroupApiUrl = config.baseUrls.userApiUrl + '/api/systemrole';
    },
  };

  user = {
    getAllInternal() {
      return `${this.userUrl}/internalUserList`;
    },
    login() {
      return `${this.userUrl}/login/`;
    },
    logout() {
      return `${this.userUrl}/logout/`;
    },
    me() {
      return `${this.userUrl}/me/`;
    },
    myGroups() {
      return `${this.userUrl}/myGroups`;
    },
    get(id: number) {
      return `${this.userUrl}/${id}`;
    },
    registerExternal() {
      return `${this.userUrl}`;
    },
    update(id: number) {
      return `${this.userUrl}/${id}`;
    },
    delete(id: number) {
      return `${this.userUrl}/${id}`;
    },
    confirmEmail(id: number) {
      return `${this.userUrl}/${id}/confirmEmail`;
    },
    getUserList() {
      return `${this.userUrl}/userList`;
    },
    getExternalUserLookup() {
      return `${this.userUrl}/externalUserLookup`;
    },
    _initialize(config: Environment) {
      this.userUrl = config.baseUrls.userApiUrl + '/api/user';
    },
  };

  directory = {
    search() {
      return `${this.directoryUrl}/search`;
    },
    getDirectoryUser(email: string) {
      return `${this.directoryUrl}/details/${email}`;
    },
    _initialize(config: Environment) {
      this.directoryUrl = config.baseUrls.userApiUrl + '/api/directory';
    },
  };

  reference = {
    create() {
      return `${this.referenceUrl}`;
    },
    getPaginatedReferenceData(referenceDataType: string) {
      return `${this.referenceUrl}/${referenceDataType}`;
    },
    activate(id: number, referenceDataTypeId: number) {
      return `${this.referenceUrl}/${referenceDataTypeId}/${id}/activate`;
    },
    deactivate(id: number, referenceDataTypeId: number) {
      return `${this.referenceUrl}/${referenceDataTypeId}/${id}/deactivate`;
    },
    increaseSortOrder(id: number, referenceDataTypeId: number) {
      return `${this.referenceUrl}/${referenceDataTypeId}/${id}/increaseSortOrder`;
    },
    decreaseSortOrder(id: number, referenceDataTypeId: number) {
      return `${this.referenceUrl}/${referenceDataTypeId}/${id}/decreaseSortOrder`;
    },
    _initialize(config: Environment) {
      this.referenceUrl = config.baseUrls.refApiUrl + '/api/referenceadministration';
    },
  };

  documentApiUrlBase: string;
  documentApiUrl: string;
  tokenApiUrl: string;

  document = {
    get: (id: number): string => `${this.documentApiUrl}/${id}/internaldetails`,
    updateDetails: (id: number): string => `${this.documentApiUrl}/${id}/internaldetails`,
    getRelatedByObjectId: () => `${this.documentApiUrl}/relatedbyObject`,
    getRelatedByEntityId: () => `${this.documentApiUrl}/relatedbyEntity`,
    getRelatedByEntityIdWithTags: () => `${this.documentApiUrl}/relatedbyEntityWithTags`,
    getRelatedByObjectIdWithTags: () => `${this.documentApiUrl}/relatedbyObjectWithTags`,
    getRelatedTagsByEntityId: () => `${this.documentApiUrl}/relatedbyEntity/tags`,
    getRelatedTagsByObjectId: () => `${this.documentApiUrl}/relatedbyObject/tags`,
    getDocumentNotificationByEntity: () => `${this.documentApiUrl}/documentNotificationbyEntity`,
    getDocumentNotificationByRecipient: (id: number) => `${this.documentApiUrl}/${id}/recipients`,
    getRelatedByParentDoc: (documentId: number) =>
      `${this.documentApiUrl}/${documentId}/related-child-docs-internal`,
    content: (documentId: number, index: number): string =>
      `${this.documentApiUrl}/${documentId}/content/${index}`,
    preview: (documentId: number, index: number): string =>
      `${this.documentApiUrl}/${documentId}/preview/${index}`,
    getDocumentFormDetails: (id: number): string => `${this.documentApiUrl}/${id}/form-details`,
    updateDocumentFormDetailsBulk: (): string => `${this.documentApiUrl}/form-details`,
    updateDocumentFormDetails: (id: number): string => `${this.documentApiUrl}/${id}/form-details`,
    metadata: (documentId: number): string => `${this.documentApiUrl}/${documentId}/metadata`,
    allMetadataByDocumentId: (id: number): string =>
      `${this.documentApiUrl}/${id}/allMetadataByDocumentId`,
    deleteDocument: (id: number): string => `${this.documentApiUrl}/${id}`,
    download: (id: number): string => `${this.documentApiUrl}/${id}/downloadinternal`,
    downloadBulk: (): string => `${this.documentApiUrl}/bulkdownloadinternal`,
    downloadRelated: (id: number): string => `${this.documentApiUrl}/${id}/downloadrelatedinternal`,
    downloadRelatedBulk: (): string => `${this.documentApiUrl}/bulkdownloadrelatedinternal`,
    recentDownloads: (): string => `${this.documentApiUrl}/recent-downloads`,
    token: (): string => this.tokenApiUrl,
    saveUploadDetails: (id: number): string => `${this.documentApiUrl}/${id}/upload`,
    saveMoveDetails: (): string => `${this.documentApiUrl}/movedocument`,
    saveMoveDetailsBulk: (): string => `${this.documentApiUrl}/bulkmovedocument`,
    getDocumentFormsByCategory: (categoryId: number): string =>
      `${this.documentApiUrlBase}/api/documentcategory/${categoryId}/forms`,
    getDocumentForms: (): string => `${this.documentApiUrlBase}/api/documentform`,
    getDocumentFormVersion: (id: number): string =>
      `${this.documentApiUrlBase}/api/documentform/${id}/versions`,
    getDocumentDistributionRecipientByDocumentId: (id: number): string =>
      `${this.documentApiUrl}/${id}/distributionRecipients`,
    search: (): string => `${this.documentApiUrl}/search`,
    _initialize: (config: Environment) => {
      this.documentApiUrlBase = config.baseUrls.documentApiUrl;
      this.documentApiUrl = config.baseUrls.documentApiUrl + '/api/document';
      this.tokenApiUrl = config.baseUrls.documentApiUrl + '/api/token';
    },
  };

  urlsForSystemType(typeCode: SystemTypeCode) {
    switch (typeCode) {
      case EntityTypeCode.Claimant:
        return this.claimant;
      case EntityTypeCode.Insurer:
        return this.insurer;
      case ObjectTypeCode.MdoReviewRequest:
        return this.medicalDirectorsOfficeReview;
      case EntityTypeCode.ThirdPartyAdministrator:
        return this.thirdPartyAdministrator;
      case EntityTypeCode.MedicalSupplier:
        return this.medicalSupplier;
      case EntityTypeCode.Employer:
        return this.employer;
      case EntityTypeCode.HealthcareProvider:
        return this.healthcareProvider;
      case ObjectTypeCode.PriorAuthorization:
        return this.priorAuthorization;
      case ObjectTypeCode.MedicalBillingDispute:
        return this.medicalBillingDispute;
      case ObjectTypeCode.Document:
        return this.document;
      default:
        throw new Error(`No URLs found for type code ${typeCode}.`);
    }
  }

  urlsForTransaction(transactionType: TransactionType) {
    switch (transactionType) {
      case TransactionType.Claim:
        return this.employer;
      default:
        throw new Error(`No URLs found for transaction type ${transactionType}.`);
    }
  }
}
